import ModelInterface from '@/models/ModelInterface';
import {RawPoll, QuestResult} from '@/types/entity/RawPoll'
import {DateTime} from 'luxon';
import {millisToFormat} from '@/lib/date'

import {
  POLL_STATUS_OPEN,
  POLL_STATUS_FINISHED,
  MAILING_STATUS_DRAFT,
  MAILING_STATUS_READY,
  MAILING_STATUS_BEFORE_PROGRESS,
  MAILING_STATUS_PROGRESS,
  MAILING_STATUS_CANCELED,
  MAILING_STATUS_COMPLETED
} from '@/constants';

// @ts-ignore
import messages from '@/lang/index';

interface IStatuses {
  [key: string]: {label: string, bgColor: string, color: string};
}

const STATUSES: IStatuses = {
  [MAILING_STATUS_DRAFT]: {label: messages.common.statuses.draft, bgColor: '#f4f4f5', color: '#909399'},
  [MAILING_STATUS_READY]: {label: messages.common.statuses.ready, bgColor: '#f4f4f5', color: '#909399'},
  [MAILING_STATUS_BEFORE_PROGRESS]: {label: messages.common.statuses.before_progress, bgColor: '#FEEDC2', color: '#E6AB45'},
  [MAILING_STATUS_PROGRESS]: {label: messages.common.statuses.progres, bgColor: '#FEEDC2', color: '#E6AB45'},
  [MAILING_STATUS_COMPLETED]: {label: messages.common.statuses.completed, bgColor: '#DFF3DD', color: '#15BF51'},
  [POLL_STATUS_FINISHED]: {label: messages.common.statuses.finished, bgColor: '#DFF3DD', color: '#15BF51'},
  [MAILING_STATUS_CANCELED]: {label: messages.common.statuses.canceled, bgColor: '#FFD9D9', color: '#FF3738'}
};

const MAILING_WEIRD_TIMEOUT = 3600000

class Poll extends ModelInterface<RawPoll> {
  constructor (data: RawPoll) {
    super(data);
  }

  public getMailingId () {
    return this.data.mailing_id;
  }

  public getTitle () {
    return this.data.title;
  }

  public getDescription () {
    return this.data.descr;
  }

  public getQuests () {
    return this.data.quests;
  }

  public getResults () {
    return this.data.results || []
  }

  public getQuestResult (qid: string): QuestResult | undefined {
    let results = this.getResults()
    return results.find(quest => quest.qid === qid);
  }

  public getAnsNum () {
    return 100
  }

  public getStatus () {
    if (this.data.status === POLL_STATUS_OPEN) {
      if (this.getSendAt() && this.getSendAt()! < DateTime.local().toMillis() && this.data.mailing.status === MAILING_STATUS_READY) {
        return MAILING_STATUS_BEFORE_PROGRESS;
      } else {
        return this.data.mailing.status;
      }
    } else {
      return this.data.status;
    }
  }

  public getSendAt () {
    return this.data.mailing.send_at;
  }

  public getFinishAt () {
    return this.data.finish_at;
  }

  public getSentErr () {
    return this.data.mailing.sent_err;
  }

  public getSentOk () {
    return this.data.mailing.sent_ok;
  }

  public getRcptNum () {
    return this.data.mailing.rcpt_num;
  }

  public getMsgrNum () {
    return this.data.mailing.msgr_num || 0;
  }

  public getSendProgress (): number {
    let progress = ((this.getSentOk() + this.getSentErr()) / this.getRcptNum()) * 100;
    return Math.round(progress);
  }

  public getStatusLabel (status?: string) {
    if (status) {
      return STATUSES[status].label || messages.labels.unknown;
    } else {
      return STATUSES[this.getStatus()].label;
    }
  }

  public getStatusColor (status?: string) {
    if (status) {
      return STATUSES[status].color || '#000';
    } else {
      return STATUSES[this.getStatus()].color;
    }
  }

  public getStatusBgColor (status?: string) {
    if (status) {
      return STATUSES[status].bgColor || '#000';
    } else {
      return STATUSES[this.getStatus()].bgColor;
    }
  }

  public getCt () {
    return this.data.ct;
  }

  public getTs () {
    return this.data.ts;
  }

  public getFilter () {
    return this.data.mailing.filter || {};
  }

  public getAnsTotal () {
    return this.data.ans_total || 0
  }

  public getCreatedFirstName () {
    return this.data.mailing.created.first_name || '';
  }

  public getCreatedLastName () {
    return this.data.mailing.created.last_name || '';
  }

  public getCreatedMiddleName () {
    return this.data.mailing.created.middle_name || '';
  }

  public getCreatedFullName () {
    return (
      this.getCreatedLastName() +
      ' ' +
      this.getCreatedFirstName() +
      ' ' +
      this.getCreatedMiddleName()
    );
  }

  public isWeird (): boolean {
    if (
      this.getStatus() === MAILING_STATUS_PROGRESS &&
      (Date.now() - this.getTs()) > MAILING_WEIRD_TIMEOUT
    ) {
      return true
    }
    return false
  }

  get title () {
    return this.getTitle();
  }

  get status () {
    return this.getStatus();
  }

  get rcpt_num () {
    return this.getRcptNum();
  }

  get ans_total () {
    return this.getAnsTotal();
  }

  get send_at_date () {
    if (this.getSendAt()) {
      return millisToFormat(this.getSendAt() as number)
    } else {
      return messages.labels.not_specified
    }
  }

  get finish_at_date () {
    if (this.getFinishAt()) {
      return millisToFormat(this.getFinishAt() as number)
    } else {
      return messages.labels.not_specified
    }
  }

  get ct_date () {
    return millisToFormat(this.getCt())
  }
}

export default Poll;